import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Select,
  Dropdown,
  Modal,
  Input,
  Divider,
} from '@prio365/prio365-react-library';
import { makePrioStyles } from '../../theme/utils';
import {
  TaskHubFilters,
  TaskHubModuleFilters,
} from './setting/TaskHubSettings.types';
import { useTaskHubSettings } from './setting/useTaskHubSettings';
import Flex from '../../components/Flex';
import { Badge } from 'antd';

const useStyles = makePrioStyles((theme) => ({
  headerContainer: {
    padding: `${theme.spacing.regular}px ${theme.spacing.large}px`,
    backgroundColor: theme.colors.application.background.default,
    borderBottom: `1px solid ${theme.colors.application.border}`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  },
  filterSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.regular,
    height: '40px',
  },
  savedFiltersContainer: {
    minWidth: 280,
    position: 'relative',
    height: '100%',
  },
  plannerSelector: {
    minWidth: 350,
    maxWidth: 500,
    flex: 1,
  },
  executeSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.small,
  },
  executeButton: {
    minWidth: 120,
    height: 40,
  },
  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.small,
    fontSize: '13px',
    color: theme.colors.application.typography.muted,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
  },
  statusSelected: {
    backgroundColor: theme.colors.base.blue.default,
  },
  statusModified: {
    backgroundColor: theme.colors.base.yellow.default,
  },
  statusLoading: {
    backgroundColor: theme.colors.base.primary.default,
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  filterBadge: {
    marginLeft: theme.spacing.small,
  },
  dropdownHeader: {
    padding: `${theme.spacing.small}px ${theme.spacing.regular}px`,
    borderBottom: `1px solid ${theme.colors.application.border}`,
    backgroundColor: theme.colors.application.background.light,
    fontSize: '12px',
    color: theme.colors.application.typography.default,
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${theme.spacing.small}px ${theme.spacing.regular}px`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.colors.application.background.hover,
    },
  },
  defaultBadge: {
    fontSize: '10px',
    padding: '2px 6px',
    backgroundColor: theme.colors.base.green['20'],
    color: theme.colors.base.green.default,
    borderRadius: theme.borderRadius.small,
    fontWeight: 500,
  },
  actionButton: {
    padding: '4px 8px',
    fontSize: '11px',
    height: 'auto',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.5 },
  },
}));

interface TaskHubHeaderProps {
  plannerOptions: Array<{
    value: string;
    label: string;
    projectId: string;
    projectName: string;
  }>;
  selectedPlannerIds: string[];
  onPlannerChange: (plannerIds: string[]) => void;
  onExecute: () => void;
  loading: boolean;
  buttonMode: 'execute' | 'refresh';
  currentFilters: TaskHubFilters;
  onFiltersChange: (filters: TaskHubModuleFilters) => void;
  executedPlannerIds: string[];
}

const TaskHubHeader: React.FC<TaskHubHeaderProps> = ({
  plannerOptions,
  selectedPlannerIds,
  onPlannerChange,
  onExecute,
  loading,
  buttonMode,
  currentFilters,
  onFiltersChange,
  executedPlannerIds,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  // Use the real settings hook
  const {
    savedQueries,
    defaultQuery,
    loading: settingsLoading,
    error: settingsError,
    saveQuery,
    deleteQuery,
    setDefaultQuery,
    loadQuery,
  } = useTaskHubSettings();

  // State for UI
  const [selectedQueryId, setSelectedQueryId] = useState<string>('');
  const [isModified, setIsModified] = useState(false);

  // Modal states
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [modalFilterName, setModalFilterName] = useState('');
  const [queryToRename, setQueryToRename] = useState<string | null>(null);

  // Set initial selected filter when queries load
  useEffect(() => {
    if (savedQueries.length > 0 && !selectedQueryId && !settingsLoading) {
      const defaultFilter = savedQueries.find(
        (q) => defaultQuery?.taskHubQueryId === q.taskHubQueryId
      );
      if (defaultFilter) {
        setSelectedQueryId(defaultFilter.taskHubQueryId);
      } else if (savedQueries.length > 0) {
        setSelectedQueryId(savedQueries[0].taskHubQueryId);
      }
    }
  }, [savedQueries, defaultQuery, selectedQueryId, settingsLoading]);

  // Check if current filters are modified compared to selected saved filter
  useEffect(() => {
    if (!selectedQueryId) {
      setIsModified(false);
      return;
    }

    const selectedFilter = savedQueries.find(
      (f) => f.taskHubQueryId === selectedQueryId
    );
    if (!selectedFilter) {
      setIsModified(false);
      return;
    }

    // Convert TaskHubQuery to TaskHubFilters for comparison
    const queryFilters: TaskHubFilters = {
      plannerIds: selectedFilter.plannerIds,
      assigneeIds: selectedFilter.assigneeIds,
      projectIds: selectedFilter.projectIds,
      priorityIds: selectedFilter.priorityIds,
      labelIds: selectedFilter.labelIds,
      statusIds: selectedFilter.statusIds,
    };

    const isEqual =
      JSON.stringify(queryFilters) === JSON.stringify(currentFilters);
    setIsModified(!isEqual);
  }, [currentFilters, selectedQueryId, savedQueries]);

  // Status indicator logic
  const statusInfo = useMemo(() => {
    if (loading) {
      return {
        text: t('taskHub:selector.loading'),
        dotClass: classes.statusLoading,
      };
    }

    const hasChanges =
      selectedPlannerIds.length !== executedPlannerIds.length ||
      !selectedPlannerIds.every((id) => executedPlannerIds.includes(id));

    if (hasChanges && selectedPlannerIds.length > 0) {
      return {
        text: t('taskHub:selector.changesNotExecuted'),
        dotClass: classes.statusModified,
      };
    }

    if (selectedPlannerIds.length > 0) {
      return {
        text: `${selectedPlannerIds.length}/15 ${t('taskHub:selector.selected')}`,
        dotClass: classes.statusSelected,
      };
    }

    return null;
  }, [loading, selectedPlannerIds, executedPlannerIds, t, classes]);

  // Active filters count
  const activeFiltersCount = useMemo(() => {
    return Object.values(currentFilters).reduce((count, filter) => {
      return count + (Array.isArray(filter) ? filter.length : 0);
    }, 0);
  }, [currentFilters]);

  // Convert TaskHubFilters to TaskHubModuleFilters
  const convertToModuleFilters = useCallback(
    (filters: TaskHubFilters): TaskHubModuleFilters => {
      return {
        assignee: filters.assigneeIds,
        project: filters.projectIds,
        priority: filters.priorityIds,
        labels: filters.labelIds,
        status: filters.statusIds,
      };
    },
    []
  );

  // Handlers
  const handleFilterSelect = useCallback(
    (queryId: string) => {
      const query = loadQuery(queryId);
      if (query) {
        setSelectedQueryId(queryId);

        // Convert query to module filters format
        const moduleFilters = convertToModuleFilters({
          plannerIds: query.plannerIds,
          assigneeIds: query.assigneeIds,
          projectIds: query.projectIds,
          priorityIds: query.priorityIds,
          labelIds: query.labelIds,
          statusIds: query.statusIds,
        });

        onFiltersChange(moduleFilters);

        // Also update planner selection
        onPlannerChange(query.plannerIds);
      }
    },
    [loadQuery, convertToModuleFilters, onFiltersChange, onPlannerChange]
  );

  const handleSaveNew = useCallback(() => {
    setSaveModalVisible(true);
    setModalFilterName('');
  }, []);

  // FIXED: Handle override with proper query replacement
  const handleOverwrite = useCallback(async () => {
    if (selectedQueryId) {
      try {
        const selectedQuery = savedQueries.find(
          (q) => q.taskHubQueryId === selectedQueryId
        );
        if (selectedQuery) {
          // Delete the old query first, then save with the same name
          await deleteQuery(selectedQueryId);
          const newQuery = await saveQuery(
            selectedQuery.displayName,
            currentFilters
          );

          if (newQuery) {
            // Update the selected query ID to the new one
            setSelectedQueryId(newQuery.taskHubQueryId);
            setIsModified(false);
          }
        }
      } catch (error) {
        console.error('Failed to overwrite filter:', error);
      }
    }
  }, [selectedQueryId, savedQueries, currentFilters, saveQuery, deleteQuery]);

  const handleRename = useCallback(
    (queryId: string) => {
      const query = savedQueries.find((q) => q.taskHubQueryId === queryId);
      if (query) {
        setQueryToRename(queryId);
        setModalFilterName(query.displayName);
        setRenameModalVisible(true);
      }
    },
    [savedQueries]
  );

  const handleSetDefault = useCallback(
    async (queryId: string) => {
      try {
        await setDefaultQuery(queryId);
      } catch (error) {
        console.error('Failed to set default filter:', error);
      }
    },
    [setDefaultQuery]
  );

  const handleDelete = useCallback(
    async (queryId: string) => {
      try {
        await deleteQuery(queryId);
        if (selectedQueryId === queryId) {
          // Select the default query or first available query
          const remainingQueries = savedQueries.filter(
            (q) => q.taskHubQueryId !== queryId
          );
          if (remainingQueries.length > 0) {
            const newDefault =
              remainingQueries.find(
                (q) => defaultQuery?.taskHubQueryId === q.taskHubQueryId
              ) || remainingQueries[0];
            handleFilterSelect(newDefault.taskHubQueryId);
          }
        }
      } catch (error) {
        console.error('Failed to delete filter:', error);
      }
    },
    [
      selectedQueryId,
      savedQueries,
      defaultQuery,
      deleteQuery,
      handleFilterSelect,
    ]
  );

  const confirmSave = useCallback(async () => {
    if (modalFilterName.trim()) {
      try {
        const newQuery = await saveQuery(
          modalFilterName.trim(),
          currentFilters
        );
        if (newQuery) {
          setSaveModalVisible(false);
          setSelectedQueryId(newQuery.taskHubQueryId);
          setIsModified(false);
        }
      } catch (error) {
        console.error('Failed to save filter:', error);
      }
    }
  }, [modalFilterName, currentFilters, saveQuery]);

  const confirmRename = useCallback(async () => {
    if (queryToRename && modalFilterName.trim()) {
      try {
        const queryToUpdate = savedQueries.find(
          (q) => q.taskHubQueryId === queryToRename
        );
        if (queryToUpdate) {
          // Delete the old query and create a new one with the new name
          await deleteQuery(queryToRename);
          const newQuery = await saveQuery(modalFilterName.trim(), {
            plannerIds: queryToUpdate.plannerIds,
            assigneeIds: queryToUpdate.assigneeIds,
            projectIds: queryToUpdate.projectIds,
            priorityIds: queryToUpdate.priorityIds,
            labelIds: queryToUpdate.labelIds,
            statusIds: queryToUpdate.statusIds,
          });

          if (newQuery) {
            // Update selected query if it was the renamed one
            if (selectedQueryId === queryToRename) {
              setSelectedQueryId(newQuery.taskHubQueryId);
            }
          }
        }
        setRenameModalVisible(false);
        setQueryToRename(null);
      } catch (error) {
        console.error('Failed to rename filter:', error);
      }
    }
  }, [
    queryToRename,
    modalFilterName,
    savedQueries,
    deleteQuery,
    saveQuery,
    selectedQueryId,
  ]);

  // Dropdown menu for saved filters
  const savedFiltersDropdown = (
    <div>
      <div className={classes.dropdownHeader}>
        {t('taskHub:settings.savedFilters')}
      </div>
      {settingsLoading ? (
        <div style={{ padding: 16, textAlign: 'center' }}>
          {t('taskHub:selector.loading')}...
        </div>
      ) : savedQueries.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', color: '#666' }}>
          {t('taskHub:settings.placeholders.selectSavedQuery')}
        </div>
      ) : (
        savedQueries.map((query) => (
          <div key={query.taskHubQueryId} className={classes.dropdownItem}>
            <div
              style={{ flex: 1, cursor: 'pointer' }}
              onClick={() => handleFilterSelect(query.taskHubQueryId)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {query.displayName}
                {defaultQuery?.taskHubQueryId === query.taskHubQueryId && (
                  <span className={classes.defaultBadge}>
                    {t('taskHub:settings.labels.default')}
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <Button
                size="small"
                type="link"
                className={classes.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRename(query.taskHubQueryId);
                }}
                iconProp={['fal', 'edit']}
              />
              {defaultQuery?.taskHubQueryId !== query.taskHubQueryId && (
                <Button
                  size="small"
                  type="link"
                  className={classes.actionButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSetDefault(query.taskHubQueryId);
                  }}
                  iconProp={['fal', 'star']}
                />
              )}
              {savedQueries.length > 1 && (
                <Button
                  size="small"
                  type="link"
                  className={classes.actionButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(query.taskHubQueryId);
                  }}
                  iconProp={['fal', 'trash']}
                />
              )}
            </div>
          </div>
        ))
      )}
      <Divider />
      <div style={{ padding: 12 }}>
        <Flex.Row childrenGap={8}>
          <Button
            size="small"
            type="primary"
            onClick={handleSaveNew}
            iconProp={['fal', 'plus']}
            style={{ flex: 1 }}
            disabled={savedQueries.length >= 10}
          >
            {t('taskHub:settings.actions.saveNew')}
          </Button>
          {isModified && selectedQueryId && (
            <Button
              size="small"
              type="default"
              onClick={handleOverwrite}
              iconProp={['fal', 'save']}
            >
              {t('taskHub:settings.actions.overwrite')}
            </Button>
          )}
        </Flex.Row>
      </div>
    </div>
  );

  const selectedQuery = selectedQueryId
    ? savedQueries.find((q) => q.taskHubQueryId === selectedQueryId)
    : null;

  return (
    <div className={classes.headerContainer}>
      <Flex.Row alignItems="center" style={{ width: '100%' }}>
        {/* Saved Filters Section */}
        <div className={classes.filterSection}>
          <div className={classes.savedFiltersContainer}>
            <Dropdown
              overlay={savedFiltersDropdown}
              overlayStyle={{ zIndex: 280 }}
              placement="bottomLeft"
              trigger="click"
            >
              <Button
                type={isModified ? 'primary' : 'default'}
                style={{
                  width: '250px',
                  height: '100%',
                  textAlign: 'left',
                }}
                iconProp={['fal', 'filter']}
                suffixIconProp={['fal', 'chevron-down']}
                loading={settingsLoading}
              >
                <span
                  style={{
                    marginRight: 8,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {selectedQuery?.displayName ||
                    t('taskHub:settings.placeholders.selectSavedQuery')}
                </span>

                {activeFiltersCount > 0 && (
                  <Badge
                    count={activeFiltersCount}
                    className={classes.filterBadge}
                    style={{ backgroundColor: '#1890ff' }}
                  />
                )}
              </Button>
            </Dropdown>
          </div>
        </div>

        {/* Planner Selector */}
        <div className={classes.plannerSelector}>
          <Select
            mode="multiple"
            showSearch
            filterOption={(input, option) => {
              const label = option?.label?.toString().toLowerCase() || '';
              const projectName = option?.projectName?.toLowerCase() || '';
              return (
                label.includes(input.toLowerCase()) ||
                projectName.includes(input.toLowerCase())
              );
            }}
            placeholder={
              plannerOptions.length === 0
                ? t('taskHub:selector.noPlannersAvailable')
                : t('taskHub:selector.selectPlannersPlaceholder')
            }
            value={selectedPlannerIds}
            onChange={onPlannerChange}
            options={plannerOptions}
            allowClear
            disabled={plannerOptions.length === 0 || loading}
            style={{ width: '100%' }}
            maxTagCount={3}
            dropdownRender={(menu) => (
              <div>
                {selectedPlannerIds.length >= 15 && (
                  <div
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#fff2e8',
                      borderBottom: '1px solid #d9d9d9',
                      color: '#fa8c16',
                      fontSize: '12px',
                    }}
                  >
                    {t('taskHub:selector.maxPlanners', { max: 15 })}
                  </div>
                )}
                {menu}
              </div>
            )}
          />
        </div>

        {/* Execute Section */}
        <div className={classes.executeSection}>
          <Button
            type="primary"
            className={classes.executeButton}
            onClick={onExecute}
            disabled={
              (buttonMode === 'execute' && selectedPlannerIds.length === 0) ||
              (buttonMode === 'refresh' && executedPlannerIds.length === 0) ||
              loading
            }
            iconProp={
              buttonMode === 'execute' ? ['fal', 'play'] : ['fal', 'sync']
            }
            loading={loading}
          >
            {loading
              ? t('taskHub:navigation.loading')
              : buttonMode === 'execute'
                ? t('taskHub:navigation.execute')
                : t('taskHub:navigation.refresh')}
          </Button>

          {/* Status Indicator */}
          {statusInfo && (
            <div className={classes.statusIndicator}>
              <div className={`${classes.statusDot} ${statusInfo.dotClass}`} />
              <span>{statusInfo.text}</span>
            </div>
          )}
        </div>
      </Flex.Row>

      {/* Settings Error Display */}
      {settingsError && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            backgroundColor: '#fff1f0',
            border: '1px solid #ffccc7',
            borderRadius: 4,
            color: '#ff4d4f',
            fontSize: '12px',
          }}
        >
          {settingsError}
        </div>
      )}

      {/* Save New Filter Modal */}
      <Modal
        visible={saveModalVisible}
        title={t('taskHub:settings.modal.saveQuery.title')}
        onClose={() => setSaveModalVisible(false)}
        okText={t('taskHub:settings.modal.saveQuery.save')}
        cancelText={t('taskHub:settings.modal.saveQuery.cancel')}
        onOk={confirmSave}
        okButtonProps={{
          disabled: !modalFilterName.trim() || savedQueries.length >= 10,
        }}
      >
        <Input
          placeholder={t('taskHub:settings.modal.saveQuery.placeholder')}
          value={modalFilterName}
          onChange={(e) => setModalFilterName(e)}
          onPressEnter={confirmSave}
          autoFocus
        />
        <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
          {t('taskHub:settings.modal.saveQuery.queriesCount', {
            current: savedQueries.length,
            max: 10,
          })}
        </div>
      </Modal>

      {/* Rename Filter Modal */}
      <Modal
        visible={renameModalVisible}
        title={t('taskHub:settings.modal.renameQuery.title')}
        onClose={() => setRenameModalVisible(false)}
        okText={t('taskHub:settings.modal.renameQuery.save')}
        cancelText={t('taskHub:settings.modal.renameQuery.cancel')}
        onOk={confirmRename}
        okButtonProps={{
          disabled: !modalFilterName.trim(),
        }}
      >
        <Input
          placeholder={t('taskHub:settings.modal.renameQuery.placeholder')}
          value={modalFilterName}
          onChange={(e) => setModalFilterName(e)}
          onPressEnter={confirmRename}
          autoFocus
          full={true}
        />
      </Modal>
    </div>
  );
};

export default TaskHubHeader;
